
import React, { useState, useEffect } from 'react';
import { getBackendService } from '../../shared/services/apiConfig';
import firestoreService from '../../shared/services/firestoreService';
import {
    getFlashPromoStatus, formatFlashPromoDate, todayISO, flashPromoWindow,
    FLASH_PROMO_STATUS_LABEL, type FlashPromoStatus,
} from '../../shared/flashPromo';
import { BoltIcon } from './icons/UiIcons';
import type { Campaign, FlashPromotion, Restaurant } from '../../shared/types';
import { Button, Badge, Modal, PageHeader, Tabs, EmptyState, tabPanelProps } from '../../shared/design';
import { SEGMENT_KEYS, SEGMENT_META, targetSegmentLabel } from '../../shared/customerSegmentation';

/** Ramène un ancien ciblage libre (« Tous les membres », « Gros paniers »…) vers une clé connue. */
const normalizeTarget = (t: string | undefined | null): string => {
    if (!t || t === 'all' || t === 'Tous les membres') return 'all';
    if (t === 'regulars') return 'habitue';
    if ((SEGMENT_KEYS as string[]).includes(t)) return t;
    if (/gros pani/i.test(t)) return 'gros_panier';
    if (/vip/i.test(t)) return 'vip';
    if (/inactif|risque/i.test(t)) return 'a_risque';
    return 'all';
};

const SegmentOptions: React.FC<{ current?: string }> = ({ current }) => (
    <>
        <option value="all">Tous les membres</option>
        {SEGMENT_KEYS.map(k => <option key={k} value={k}>{SEGMENT_META[k].label}</option>)}
        {current && current !== 'all' && !(SEGMENT_KEYS as string[]).includes(current) && (
            <option value={current}>{current}</option>
        )}
    </>
);

const STATUS_VARIANT: Record<FlashPromoStatus, 'success' | 'info' | 'warning' | 'neutral' | 'error'> = {
    live: 'success', upcoming: 'info', soldout: 'warning', ended: 'neutral', stopped: 'error',
};

const pad = (n: number) => String(n).padStart(2, '0');
const fmtCountdown = (ms: number) => {
    const s = Math.max(0, Math.floor(ms / 1000));
    return `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
};
const euro = (n: number) => `${n.toLocaleString('fr-FR', { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 })} €`;
const CHANNEL_LABEL: Record<string, string> = { Push: 'Push', SMS: 'SMS', Email: 'E-mail', reactivation: 'Relance', flash: 'Flash' };

/** Horloge locale : seul le composant qui l'utilise est re-rendu à chaque tic. */
const useNow = (intervalMs: number) => {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), intervalMs);
        return () => clearInterval(t);
    }, [intervalMs]);
    return now;
};

/**
 * Carte « vente flash » telle que le client la voit dans l'app, avec le
 * compte à rebours et le stock restant — comme sur la fiche du site.
 * Le compte à rebours a sa propre horloge (1 s) : la page et ses formulaires ne
 * sont plus re-rendus chaque seconde.
 */
const FlashCard: React.FC<{ promo: FlashPromotion; onEdit: () => void; onStop: () => void; onDelete: () => void }> = ({ promo, onEdit, onStop, onDelete }) => {
    const now = useNow(1000);
    const status = getFlashPromoStatus(promo, new Date(now));
    const win = flashPromoWindow(promo);
    const remaining = promo.quantityRemaining ?? promo.quantityTotal;
    const pct = promo.quantityTotal ? Math.round((remaining / promo.quantityTotal) * 100) : 0;
    const countdown = win ? (status === 'live' || status === 'soldout' ? win.end.getTime() - now : win.start.getTime() - now) : 0;
    const live = status === 'live' || status === 'soldout';
    return (
        <div className="relative overflow-hidden rounded-3xl border border-black/[0.06] bg-white p-5 shadow-sm">
            <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-brand-500/10 blur-2xl" aria-hidden="true" />
            <div className="relative flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                        <Badge variant={STATUS_VARIANT[status]} size="sm" dot={status === 'live'}>{FLASH_PROMO_STATUS_LABEL[status]}</Badge>
                        <span className="text-xs text-gray-500">{formatFlashPromoDate(promo)} · {promo.startTime} → {promo.endTime}</span>
                        {promo.targetSegment && promo.targetSegment !== 'all' && (
                            <span className="text-xs text-gray-500">· {targetSegmentLabel(promo.targetSegment)}</span>
                        )}
                    </div>
                    <p className="mt-2 font-display text-2xl text-gray-900">{promo.itemName}</p>
                    <p className="mt-1 flex items-baseline gap-2">
                        <span className="text-sm text-gray-400 line-through">{euro(promo.originalPrice)}</span>
                        <span className="font-stat text-3xl text-brand-600">{euro(promo.discountPrice)}</span>
                    </p>
                    <div className="mt-3 max-w-sm">
                        <div className="h-2 rounded-full bg-cream-200">
                            <div className={`h-2 rounded-full ${remaining > 0 ? 'bg-brand-500' : 'bg-gray-300'}`} style={{ width: `${pct}%` }} />
                        </div>
                        <p className="mt-1.5 text-xs font-semibold text-gray-700">{remaining} restante{remaining > 1 ? 's' : ''} sur {promo.quantityTotal}</p>
                    </div>
                </div>
                <div className="flex flex-col items-start gap-3 sm:items-end">
                    {win && (status === 'live' || status === 'soldout' || status === 'upcoming') && (
                        <div className="sm:text-right">
                            <p className="eyebrow">{live ? 'Se termine dans' : 'Commence dans'}</p>
                            <p className="font-stat text-3xl leading-none text-brand-600 tabular-nums">{fmtCountdown(countdown)}</p>
                        </div>
                    )}
                    <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant="outline" onClick={onEdit}>{status === 'ended' || status === 'stopped' ? 'Relancer' : 'Modifier'}</Button>
                        {live || status === 'upcoming' ? <Button size="sm" variant="ghost" onClick={onStop}>Arrêter</Button> : null}
                        <Button size="sm" variant="ghost" onClick={onDelete} className="!text-brand-600">Supprimer</Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const emptyFlashForm = () => ({
    menuItemId: '', discountPrice: 0, quantityTotal: 20, date: todayISO(),
    startTime: '19:00', endTime: '22:00', targetSegment: 'all'
});

const MarketingCampaigns: React.FC = () => {
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [flashPromos, setFlashPromos] = useState<FlashPromotion[]>([]);
    const [isWizardOpen, setIsWizardOpen] = useState(false);
    const [isFlashOpen, setIsFlashOpen] = useState(false);
    const [editingPromoId, setEditingPromoId] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
    const [tab, setTab] = useState<'flash' | 'campaigns'>('flash');
    const [showHistory, setShowHistory] = useState(false);
    // Classement en cours / historique : une précision de 30 s suffit (créneaux à la minute).
    const now = useNow(30_000);

    const [formData, setFormData] = useState({
        name: '', type: 'Push' as any, description: '',
        targetSegment: 'all'
    });

    // Flash Promo state
    const [flashData, setFlashData] = useState(emptyFlashForm);

    useEffect(() => {
        let unsubscribe: (() => void) | undefined;
        let isMounted = true;

        const load = async () => {
            try {
                const backend = await getBackendService();
                const user = backend.getCurrentUser();
                if (!user?.restaurantId || !isMounted) return;

                const r = await backend.getRestaurant(
                    user.restaurantId
                );
                if (r && isMounted) {
                    setRestaurant(r);
                }

                const campaigns = await backend.getCampaigns(user.restaurantId);
                if (isMounted) setCampaigns(campaigns);

                const promos = await ((backend as any).getRestaurantFlashPromotions
                    ? (backend as any).getRestaurantFlashPromotions(user.restaurantId)
                    : firestoreService.getRestaurantFlashPromotions(user.restaurantId));
                if (isMounted) setFlashPromos(promos);

                // Une seule inscription : load() est rappelé à chaque notification. Écran quitté
                // pendant les `await` : pas d'abonnement orphelin (le nettoyage est déjà passé).
                if (isMounted && backend.subscribe && !unsubscribe) {
                    unsubscribe = backend.subscribe(load);
                }
            } catch (error) {
                console.error('Error loading campaigns:', error);
            }
        };

        load();

        return () => {
            isMounted = false;
            if (unsubscribe) unsubscribe();
        };
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            if (!restaurant?.id) {
                throw new Error('Restaurant non trouvé');
            }
            const backend = await getBackendService();
            await backend.createCampaign({
                ...formData,
                restaurantId: restaurant.id,
                status: 'active'
            });
            setIsWizardOpen(false);

            // Recharger les campagnes
            const updatedCampaigns = await backend.getCampaigns(restaurant.id);
            setCampaigns(updatedCampaigns);
        } catch (error: any) {
            console.error('Error creating campaign:', error);
            alert(error.message || 'Erreur lors de la création de la campagne');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleFlashSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const item = restaurant?.menu?.find(m => m.id === flashData.menuItemId);
        if (!item) {
            alert('Veuillez sélectionner un plat');
            return;
        }

        if (!restaurant?.id) {
            alert('Restaurant non trouvé');
            return;
        }

        if (!(flashData.discountPrice > 0) || flashData.discountPrice >= Number(item.price)) {
            alert(`Le prix promo doit être supérieur à 0 et inférieur au prix normal (${item.price}€)`);
            return;
        }
        if (!(flashData.quantityTotal > 0)) {
            alert('La quantité doit être d\'au moins 1');
            return;
        }
        if (flashData.startTime === flashData.endTime) {
            alert('L\'heure de fin doit être différente de l\'heure de début');
            return;
        }

        const fields = {
            menuItemId: item.id,
            itemName: item.name,
            originalPrice: Number(item.price),
            discountPrice: flashData.discountPrice,
            quantityTotal: flashData.quantityTotal,
            date: flashData.date,
            startTime: flashData.startTime,
            endTime: flashData.endTime,
            targetSegment: flashData.targetSegment,
        };
        if (getFlashPromoStatus({ active: true, ...fields }) === 'ended') {
            alert('Ces horaires sont déjà passés : choisissez une date ou une heure de fin future');
            return;
        }

        setIsSubmitting(true);
        try {
            const backend = await getBackendService();
            if (editingPromoId) {
                await ((backend as any).updateFlashPromotion ? (backend as any) : firestoreService).updateFlashPromotion(editingPromoId, fields);
            } else {
                await backend.createFlashPromotion({
                    ...fields,
                    restaurantId: restaurant.id,
                    quantityRemaining: flashData.quantityTotal,
                    active: true,
                } as any);
            }
            setIsFlashOpen(false);
            setEditingPromoId(null);
        } catch (error: any) {
            console.error('Error saving flash promotion:', error);
            alert(error.message || 'Erreur lors de l\'enregistrement de la vente flash');
        } finally {
            setIsSubmitting(false);
        }
    };

    const openFlashCreate = () => {
        setEditingPromoId(null);
        setFlashData(emptyFlashForm());
        setIsFlashOpen(true);
    };

    const openFlashEdit = (promo: FlashPromotion) => {
        setEditingPromoId(promo.id);
        setFlashData({
            menuItemId: promo.menuItemId,
            discountPrice: promo.discountPrice,
            quantityTotal: promo.quantityTotal,
            date: promo.date || todayISO(),
            startTime: promo.startTime,
            endTime: promo.endTime,
            targetSegment: normalizeTarget(promo.targetSegment),
        });
        setIsFlashOpen(true);
    };

    const handleFlashStop = async (promo: FlashPromotion) => {
        if (!window.confirm(`Arrêter la vente flash "${promo.itemName}" ? Elle disparaîtra de l'app.`)) return;
        try {
            const backend = await getBackendService();
            await ((backend as any).stopFlashPromotion ? (backend as any) : firestoreService).stopFlashPromotion(promo.id);
        } catch (error: any) {
            alert(error.message || 'Erreur lors de l\'arrêt de la vente flash');
        }
    };

    const handleFlashDelete = async (promo: FlashPromotion) => {
        if (!window.confirm(`Supprimer définitivement la vente flash "${promo.itemName}" ?`)) return;
        try {
            const backend = await getBackendService();
            await ((backend as any).deleteFlashPromotion ? (backend as any) : firestoreService).deleteFlashPromotion(promo.id);
        } catch (error: any) {
            alert(error.message || 'Erreur lors de la suppression de la vente flash');
        }
    };

    const activeFlash = flashPromos.filter(p => ['live', 'upcoming', 'soldout'].includes(getFlashPromoStatus(p, new Date(now))))
        .sort((a, b) => (flashPromoWindow(a)?.start.getTime() ?? 0) - (flashPromoWindow(b)?.start.getTime() ?? 0));
    const pastFlash = flashPromos.filter(p => !activeFlash.includes(p))
        .sort((a, b) => (flashPromoWindow(b)?.start.getTime() ?? 0) - (flashPromoWindow(a)?.start.getTime() ?? 0));
    const selectedItem = restaurant?.menu?.find(m => m.id === flashData.menuItemId);
    const field = 'w-full rounded-xl border border-gray-200 bg-white px-3 h-11 text-[15px] outline-none focus:border-brand-500 focus:ring-4 focus:ring-brand-500/10';
    const label = 'block text-xs font-semibold text-gray-600 mb-1.5';

    return (
        <div className="space-y-6 sm:space-y-8">
            <PageHeader
                title="Campagnes & promos"
                subtitle="Une vente flash remplit un créneau calme ; une campagne parle à un segment précis. Les deux partent dans l'app."
                actions={
                    tab === 'flash'
                        ? <Button variant="primary" onClick={openFlashCreate} leftIcon={<BoltIcon className="h-4 w-4" />}>Nouvelle vente flash</Button>
                        : <Button variant="primary" onClick={() => setIsWizardOpen(true)}>Nouvelle campagne</Button>
                }
            />

            <Tabs
                tabs={[
                    { id: 'flash', label: 'Ventes flash', badge: activeFlash.length },
                    { id: 'campaigns', label: 'Campagnes', badge: campaigns.length },
                ]}
                activeTab={tab}
                onChange={(id) => setTab(id as 'flash' | 'campaigns')}
                idPrefix="promos"
                ariaLabel="Type d'offre"
            />

            {tab === 'flash' && (
                <div {...tabPanelProps('promos', 'flash')} className="space-y-6 animate-fade-in">
                    {/* Création / modification en 4 réglages */}
                    {isFlashOpen && (
                        <form onSubmit={handleFlashSubmit} className="rounded-3xl border border-brand-200 bg-white p-5 shadow-sm sm:p-6">
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <p className="section-title">{editingPromoId ? 'Modifier la vente flash' : 'Créée en 4 réglages'}</p>
                                    <p className="mt-0.5 text-sm text-gray-500">Un plat de votre carte, un prix, une quantité, un créneau. Elle s'affiche dans l'app avec prix barré, stock restant et heure de fin.</p>
                                </div>
                                <Button type="button" size="sm" variant="ghost" onClick={() => { setIsFlashOpen(false); setEditingPromoId(null); }}>Annuler</Button>
                            </div>
                            <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
                                <div>
                                    <label className={label} htmlFor="flash-item">Plat</label>
                                    <select id="flash-item" required className={field} value={flashData.menuItemId} onChange={e => setFlashData({ ...flashData, menuItemId: e.target.value })}>
                                        <option value="">Choisir dans la carte…</option>
                                        {restaurant?.menu?.map(item => <option key={item.id} value={item.id}>{item.name} ({euro(Number(item.price))})</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className={label} htmlFor="flash-price">Prix promo{selectedItem ? ` (au lieu de ${euro(Number(selectedItem.price))})` : ''}</label>
                                    <div className="relative">
                                        <input id="flash-price" type="number" step="0.5" min="0" required className={`${field} pr-8`} value={flashData.discountPrice || ''} placeholder="7" onChange={e => setFlashData({ ...flashData, discountPrice: parseFloat(e.target.value) || 0 })} />
                                        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">€</span>
                                    </div>
                                </div>
                                <div>
                                    <label className={label} htmlFor="flash-qty">Quantité max.</label>
                                    <input id="flash-qty" type="number" min="1" required className={field} value={flashData.quantityTotal || ''} onChange={e => setFlashData({ ...flashData, quantityTotal: parseInt(e.target.value) || 0 })} />
                                </div>
                                <div className="md:col-span-2">
                                    <label className={label}>Créneau</label>
                                    <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-[1fr_auto_auto] sm:items-center">
                                        <input aria-label="Date" required type="date" min={todayISO()} className={`${field} !px-2 text-sm`} value={flashData.date} onChange={e => setFlashData({ ...flashData, date: e.target.value })} />
                                        <input aria-label="Début" required type="time" className={`${field} !px-2 text-sm sm:!w-[6.5rem]`} value={flashData.startTime} onChange={e => setFlashData({ ...flashData, startTime: e.target.value })} />
                                        <input aria-label="Fin" required type="time" className={`${field} !px-2 text-sm sm:!w-[6.5rem]`} value={flashData.endTime} onChange={e => setFlashData({ ...flashData, endTime: e.target.value })} />
                                    </div>
                                </div>
                            </div>
                            <div className="mt-5 flex flex-col gap-4 border-t border-black/[0.06] pt-5 sm:flex-row sm:items-end sm:justify-between">
                                <div className="w-full sm:max-w-xs">
                                    <label className={label} htmlFor="flash-target">Prévenir</label>
                                    <select id="flash-target" className={field} value={flashData.targetSegment} onChange={e => setFlashData({ ...flashData, targetSegment: e.target.value })}>
                                        <SegmentOptions current={flashData.targetSegment} />
                                    </select>
                                    <p className="mt-1 text-xs text-gray-500">Une notification push part aux membres ciblés au lancement.</p>
                                </div>
                                <Button type="submit" variant="primary" size="lg" loading={isSubmitting} disabled={isSubmitting || !flashData.menuItemId}>
                                    {editingPromoId ? 'Enregistrer' : 'Lancer la vente flash'}
                                </Button>
                            </div>
                        </form>
                    )}

                    {/* En cours / à venir */}
                    {activeFlash.length > 0 ? (
                        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                            {activeFlash.map(promo => (
                                <FlashCard key={promo.id} promo={promo} onEdit={() => openFlashEdit(promo)} onStop={() => handleFlashStop(promo)} onDelete={() => handleFlashDelete(promo)} />
                            ))}
                        </div>
                    ) : !isFlashOpen && (
                        <div className="rounded-3xl border border-black/[0.06] bg-white shadow-sm">
                            <EmptyState
                                icon={<BoltIcon />}
                                title="Aucune vente flash en cours"
                                description="Mettez un plat en vente flash : il s'affiche dans l'app avec prix barré, stock restant et heure de fin. Idéal pour remplir un service calme."
                                action={{ label: 'Créer une vente flash', onClick: openFlashCreate }}
                            />
                        </div>
                    )}

                    {/* Historique */}
                    {pastFlash.length > 0 && (
                        <div className="rounded-3xl border border-black/[0.06] bg-white shadow-sm">
                            <button type="button" onClick={() => setShowHistory(v => !v)} className="flex w-full items-center justify-between px-5 py-4 text-left" aria-expanded={showHistory}>
                                <span className="section-title">Ventes passées <span className="ml-1 text-sm font-normal text-gray-500">({pastFlash.length})</span></span>
                                <svg className={`h-5 w-5 text-gray-400 transition-transform ${showHistory ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                            </button>
                            {showHistory && (
                                <ul className="divide-y divide-black/[0.06] border-t border-black/[0.06]">
                                    {pastFlash.map(promo => {
                                        const status = getFlashPromoStatus(promo, new Date(now));
                                        const sold = promo.quantityTotal - (promo.quantityRemaining ?? promo.quantityTotal);
                                        return (
                                            <li key={promo.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-3 text-sm">
                                                <span className="min-w-[10rem] font-semibold text-gray-900">{promo.itemName}</span>
                                                <span className="text-gray-500">{formatFlashPromoDate(promo)} · {promo.startTime}–{promo.endTime}</span>
                                                <span className="text-gray-700"><b>{euro(promo.discountPrice)}</b> <s className="text-gray-400">{euro(promo.originalPrice)}</s></span>
                                                <span className="text-gray-500">{sold} vendue{sold > 1 ? 's' : ''} / {promo.quantityTotal}</span>
                                                <Badge variant={STATUS_VARIANT[status]} size="sm">{FLASH_PROMO_STATUS_LABEL[status]}</Badge>
                                                <span className="ml-auto flex gap-1">
                                                    <Button size="sm" variant="ghost" onClick={() => openFlashEdit(promo)}>Relancer</Button>
                                                    <Button size="sm" variant="ghost" className="!text-brand-600" onClick={() => handleFlashDelete(promo)}>Supprimer</Button>
                                                </span>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>
                    )}
                </div>
            )}

            {tab === 'campaigns' && (
                <div {...tabPanelProps('promos', 'campaigns')} className="animate-fade-in rounded-3xl border border-black/[0.06] bg-white shadow-sm">
                    {campaigns.length === 0 ? (
                        <EmptyState
                            title="Aucune campagne"
                            description="Envoyez un message push, SMS ou e-mail à un segment de vos clients : habitués, gros paniers, clients à risque…"
                            action={{ label: 'Nouvelle campagne', onClick: () => setIsWizardOpen(true) }}
                        />
                    ) : (
                        <ul className="divide-y divide-black/[0.06]">
                            {campaigns.map(camp => (
                                <li key={camp.id} className="grid grid-cols-1 gap-3 px-5 py-4 sm:grid-cols-[1fr_auto_auto_auto] sm:items-center sm:gap-6">
                                    <div className="min-w-0">
                                        <p className="font-semibold text-gray-900">{camp.name}</p>
                                        <p className="mt-0.5 truncate text-sm text-gray-500">{camp.description}</p>
                                    </div>
                                    <Badge variant={camp.type === 'flash' ? 'secondary' : 'primary'} size="sm">{CHANNEL_LABEL[camp.type] ?? camp.type}</Badge>
                                    <span className="text-sm text-gray-600">{targetSegmentLabel(camp.targetSegment)}</span>
                                    <span className="font-stat text-lg text-success sm:text-right">+{euro(camp.stats?.revenue ?? 0)}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}

            {/* Nouvelle campagne */}
            <Modal isOpen={isWizardOpen} onClose={() => setIsWizardOpen(false)} title="Nouvelle campagne">
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className={label} htmlFor="camp-name">Nom</label>
                        <input id="camp-name" required type="text" className={field} value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="Ex. : Offre week-end" />
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                            <label className={label} htmlFor="camp-target">À qui</label>
                            <select id="camp-target" className={field} value={formData.targetSegment} onChange={e => setFormData({ ...formData, targetSegment: e.target.value })}>
                                <SegmentOptions current={formData.targetSegment} />
                            </select>
                            <p className="mt-1 text-xs text-gray-500">Segments calculés chaque nuit (voir Clients).</p>
                        </div>
                        <div>
                            <label className={label} htmlFor="camp-channel">Canal</label>
                            <select id="camp-channel" className={field} value={formData.type} onChange={e => setFormData({ ...formData, type: e.target.value as any })}>
                                <option value="Push">Notification push</option><option value="SMS">SMS</option><option value="Email">E-mail</option>
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className={label} htmlFor="camp-msg">Message</label>
                        <textarea id="camp-msg" required className={`${field} h-auto py-2.5`} rows={3} value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} placeholder="Ce soir, dessert offert pour toute table réservée depuis l'app." />
                    </div>
                    <Button type="submit" variant="primary" size="lg" fullWidth loading={isSubmitting} disabled={isSubmitting}>Lancer la campagne</Button>
                </form>
            </Modal>
        </div>
    );
};
export default MarketingCampaigns;
